/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';

import { Avatar, Link, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import { noheadshotUrl } from 'bundles/partners-graphql/constants';

type Instructor = {
  id: string;
  fullName: string;
  photo?: string | null;
  shortName?: string | null;
  title?: string | null;
};

type Props = {
  instructor: Instructor;
};

const getInstructorUrl = (instructor: Instructor): string => {
  if (instructor.shortName) {
    return `/instructor/${instructor.shortName}`;
  }
  return `/instructor/~${instructor.id}`;
};

const styles = {
  card: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: var(--cds-spacing-50);
    padding: var(--cds-spacing-100);
    width: 220px;
    min-height: 146px;
    height: 100%;
    flex-shrink: 0;
    text-decoration: none;
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
    transition: box-shadow 0.2s ease-in-out;

    ${breakpoints.down('sm')} {
      width: 160px;
      min-height: 180px;
    }

    &:hover {
      text-decoration: none;
      box-shadow: var(--cds-elevation-level1);
    }

    &:focus {
      text-decoration: none;
      outline: none;
    }

    &:focus-visible {
      outline: 1px solid transparent;
      outline-offset: 2px;
      box-shadow:
        0 0 0 1px var(--cds-color-interactive-stroke-primary-focus),
        0 0 0 2px var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,
  avatar: css`
    width: 60px;
    height: 60px;
    flex-shrink: 0;
  `,
  name: css`
    color: var(--cds-color-interactive-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  `,
  title: css`
    color: var(--cds-color-neutral-primary-weak);
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;

    ${breakpoints.down('sm')} {
      -webkit-line-clamp: 4;
    }
  `,
};

const InstructorCard = ({ instructor }: Props) => {
  const instructorUrl = getInstructorUrl(instructor);
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  return (
    <Link href={instructorUrl} css={styles.card} variant="quiet" data-testid="instructor-link">
      <Avatar
        variant="user"
        size={64}
        css={styles.avatar}
        data-testid="instructor-photo"
        renderImage={() => (
          <Imgix
            src={instructor.photo ?? noheadshotUrl}
            alt=""
            maxWidth={64}
            maxHeight={64}
            imgParams={{ fit: 'crop' }}
          />
        )}
      />
      <Typography2
        component="span"
        variant={isMobile ? 'subtitleSmall' : 'subtitleMedium'}
        css={styles.name}
        data-testid="instructor-details"
      >
        {instructor.fullName}
      </Typography2>
      {instructor.title && (
        <Typography2 component="span" variant="bodyTertiary" css={styles.title}>
          {instructor.title}
        </Typography2>
      )}
    </Link>
  );
};

export default InstructorCard;
